/* eslint-disable react/require-default-props */
import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Field } from 'react-form';
import PropTypes, { any } from 'prop-types';

import InfoButton from '../../InfoButton';
import useModerateText from '../useModerateText';

import styles from './Input.scss';
import links from '../../../../config/links';

const USAPIDQuestion = 'Enter your USA Pickleball member ID *';
const requiredInputError = 'This field is required.';

const FormInput = (props) => {
    const { controlled, applyCustomStyle, useCustomErrorIndicator } = props;

    // Automatically disable profanity check for input types that can't contain profanity
    // This includes: number, tel (phone), date/time inputs, and hidden fields
    // Developers can still explicitly set disableProfanityCheck=true for other cases (email, password, etc.)
    const safeInputTypes = ['number', 'tel', 'date', 'time', 'datetime-local', 'hidden'];
    const shouldDisableProfanityCheck = props.disableProfanityCheck || safeInputTypes.includes(props.type);

    const { validateSync, validateAsync } = useModerateText({
        validateFn: props.validate,
        asyncValidateFn: props.asyncValidate,
        disableProfanityCheck: shouldDisableProfanityCheck,
    });

    const renderers = {
        link: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer">
                {children}
            </a>
        ),
      };

    const renderControlled = () => {
        const {
            description,
            formGroupCssClass,
            hiddenLabel,
            id,
            label,
            hoverText,
            value,
            themeV3,
            'data-testid': dataTestId,
            ...rest
        } = props;

        const inputProps = {
            ...rest,
            id,
            className: `form-control`,
            value: value || '',
        };

        return (
            <div className={`form-group form-group-xl ${styles.formInput} ${description && 'description'} ${themeV3 ? styles.themeV3 : ''} ${formGroupCssClass} ${props.type === 'hidden' ? 'd-none' : ''}`} data-testid={dataTestId || undefined}>
                <label htmlFor={id} className={hiddenLabel ? 'sr-only' : ''}>
                    {label}
                    {hoverText && <InfoButton hoverText={hoverText} />}
                </label>
                {description && (<small>{description}</small>)}
                <div className="input-wrapper">
                    <input {...inputProps} data-testid={dataTestId && `${dataTestId}.field`} />
                    {!themeV3 && <div className="error-indicator" />}
                </div>
            </div>
        );
    };

    const renderUncontrolled = () => {
        return (
            <Field field={props.field} validate={validateSync} asyncValidate={(value) => validateAsync(value, props.submitting)}>
                {(fieldApi) => {
                    const { onChange, customInputClass, onBlur, onPaste, onKeyDown, field, validate, asyncValidate, id, label, hiddenLabel, description, asyncErrors, formGroupCssClass, hoverText, disabled, themeV3, 'data-testid': dataTestId, ...rest } = props;
                    const { value, error, warning, touched, setValue, setTouched } = fieldApi;

                    const displayError = error || (asyncErrors && (asyncErrors.answer || asyncErrors?.[field]));

                    let errorIndicatorClass;
                    if (label === USAPIDQuestion && displayError && displayError !== requiredInputError) {
                        errorIndicatorClass = styles.USAPIdInput;
                    } else if (useCustomErrorIndicator && displayError) {
                        errorIndicatorClass = styles.customErrorIndicator;
                    } else {
                        errorIndicatorClass = 'error-indicator';
                    }
                    const inputProps = {
                        ...rest,
                        id,
                        className: `form-control ${styles.formControl} ${applyCustomStyle ? styles.customFormControl : ''} ${customInputClass}`,
                        value: value !== undefined && value !== null ? value : '',
                        onChange: e => {
                            setValue(e.target.value);
                            if (onChange) {
                                onChange(e.target.value, e);
                            }
                        },
                        onBlur: e => {
                            // Try to warm the cache so we don't need to delay submission
                            validateAsync(e.target.value, props.submitting !== null);
                            setTouched();
                            if (onBlur) {
                                onBlur(e);
                            }
                        },
                        onPaste: e => {
                            if (onPaste) {
                                onPaste(e.target.value, e);
                            }
                        },
                        onKeyDown: e => {
                            if (onKeyDown) {
                                onKeyDown(e.target.value, e);
                            }
                        },
                    };

                    return (
                        <div className={`form-group form-group-xl ${styles.formInput} ${(touched && displayError) && `${styles.isInvalid} is-invalid`} ${themeV3 ? styles.themeV3 : ''} ${description && 'description'} ${formGroupCssClass} ${props.type === 'hidden' ? 'd-none' : ''}`} data-testid={dataTestId || undefined}>
                            {!props.themeV3 && props.label && (
                                <label htmlFor={id} className={hiddenLabel ? 'sr-only' : ''}>
                                    {label}
                                    {label === USAPIDQuestion ? (
                                        <p>If you need to sign up or find your member ID, <a href={links.usapPickleballSignup} target="_blank" rel="noopener noreferrer">click here</a>.</p>
                                    ) : null}
                                    {hoverText && <InfoButton hoverText={hoverText} small />}
                                </label>
                            )}
                            {description && (<small>{description}</small>)}
                            <div className="input-wrapper">
                                <input {...inputProps} disabled={disabled} data-testid={dataTestId && `${dataTestId}.field`} />
                                {!themeV3 && (<div className={errorIndicatorClass} />)}
                                {props.themeV3 && props.label && (
                                    <label>{props.label}</label>
                                )}
                            </div>
                            {touched &&
                                ((displayError && <div className="invalid-feedback" data-testid={dataTestId && `${dataTestId}.error`}><ReactMarkdown renderers={renderers}>{displayError}</ReactMarkdown></div>) ||
                                    (warning && <div className="warning-feedback" data-testid={dataTestId && `${dataTestId}.warning`}>{warning}</div>))}
                        </div>
                    );
                }}
            </Field>
        );
    };

    if (controlled === true) {
        return renderControlled();
    }
    return renderUncontrolled();
};

const ControlledInput = (props) => {
    const { id, label, hiddenLabel, description, themeV3, asyncErrors, formGroupCssClass, hoverText, disabled, error, 'data-testid': dataTestId, ...rest } = props;
    const inputProps = {
        ...rest,
        id,
        className: `form-control`,
    };

    return (
        <div className={`form-group form-group-xl ${description && 'description'} ${themeV3 ? styles.themeV3 : ''} ${error ? 'is-invalid' : ''} ${formGroupCssClass} ${props.type === 'hidden' ? 'd-none' : ''}`} data-testid={dataTestId || undefined}>
            <label htmlFor={id} className={hiddenLabel ? 'sr-only' : ''}>
                {label}
                {hoverText && <InfoButton hoverText={hoverText} />}
            </label>
            {description && (<small>{description}</small>)}
            <div className="input-wrapper">
                <input {...inputProps} disabled={disabled} data-testid={dataTestId && `${dataTestId}.field`} />
                <div className="error-indicator" />
            </div>
            {error && <div className="invalid-feedback" data-testid={dataTestId && `${dataTestId}.error`}>{error}</div>}
        </div>
    );
};

const Input = ({ controlled, ...restProps }) => {
    if (controlled === true) {
        return <ControlledInput {...restProps} />;
    }
    return <FormInput applyCustomStyle={true} useCustomErrorIndicator={true} {...restProps} />;
};

Input.propTypes = {
    themeV3: PropTypes.bool,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    field: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
    ]),
    validate: PropTypes.func,
    id: PropTypes.string,
    label: PropTypes.string,
    hiddenLabel: PropTypes.bool,
    description: PropTypes.string,
    hoverText: PropTypes.string,
    defaultValue: PropTypes.string,
    disableProfanityCheck: PropTypes.bool,
    asyncValidate: PropTypes.func,
    asyncErrors: PropTypes.objectOf(any),
    submitting: PropTypes.bool,
    applyCustomStyle: PropTypes.bool,
    useCustomErrorIndicator: PropTypes.bool,
    'data-testid': PropTypes.string,
};

ControlledInput.propTypes = {
    ...Input.propTypes,
    error: PropTypes.string,
};

Input.defaultProps = {
    validate: () => { },
    onChange: () => { },
    onBlur: () => { },
    themeV3: false,
    field: '',
    id: '',
    label: '',
    hiddenLabel: false,
    description: '',
    hoverText: '',
    defaultValue: '',
    disableProfanityCheck: false,
    asyncErrors: {},
    asyncValidate: () => { },
    applyCustomStyle: false,
    useCustomErrorIndicator: false,
    submitting: null,
    'data-testid': null,
};

ControlledInput.defaultProps = {
    ...Input.defaultProps,
    error: null,
};

export default Input;
